import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Paper,
  Box,
  Divider,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  FormHelperText,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CategoryIcon from '@mui/icons-material/Category';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import DescriptionIcon from '@mui/icons-material/Description';
import PercentIcon from '@mui/icons-material/Percent';
import PhotoCameraBackIcon from '@mui/icons-material/PhotoCameraBack';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ProductDescriptionEditor from '../../e-commerce/components/ProductDescriptionEditor';
import { sanitizeRichText, isRichTextEmpty } from '../../../utils/richText';

// Stok ürün arayüzü - Inventory.tsx dosyasındaki ile aynı
interface InventoryItem {
  id: string;
  name: string;
  barcode?: string;
  categoryId?: string | null;
  brandId?: string | null;
  stockCode: string;
  // ⚠️ DEPRECATED: Depo stok yönetimi WarehouseStockDto ile yapılacak
  // quantity: number; // Geçici - şimdilik prop'tan gelirse kullanılacak
  // warehouseId: string | null; // Geçici - şimdilik prop'tan gelirse kullanılacak
  unit: string;
  purchasePrice: number;
  purchaseCurrency?: string;
  salePrice: number;
  saleCurrency?: string;
  listPrice?: number;
  listCurrency?: string;
  vatRate?: number;
  vatStatus?: string;
  sctRate?: number;
  sctStatus?: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  parentItemId?: string | null;
  // LEGACY UYUM: bu alan salt uyumluluk icindir.
  // Parent/child kararinda parentItemId + isVariantParent esas alinir.
  isMainItem?: boolean;
  canBeNegative?: boolean;
  departmentNo?: number | null;
  imageUrl?: string | null;
  // ✅ YENİ: Depo stokları listesi (opsiyonel)
  warehouseStocks?: {
    id: string;
    productId: string;
    warehouseId: string;
    warehouseName?: string;
    quantity: number;
    createdAt: string;
    updatedAt?: string;
  }[];
  variants?: {
    physical?: {
      color?: string | null;
      size?: string | null;
      length?: number | null;
      width?: number | null;
      height?: number | null;
      weight?: number | null;
      thickness?: number | null;
    } | null;
    technical?: {
      material?: string | null;
      grade?: string | null;
      model?: string | null;
      series?: string | null;
      power?: number | null;
      voltage?: number | null;
    } | null;
    design?: {
      pattern?: string | null;
      style?: string | null;
      finish?: string | null;
      shape?: string | null;
    } | null;
    capacity?: {
      capacity?: number | null;
      diameter?: number | null;
      innerSize?: string | null;
      outerSize?: string | null;
    } | null;
    usage?: {
      ageGroup?: string | null;
      gender?: string | null;
      season?: string | null;
      usage?: string | null;
    } | null;
    variety?: {
      flavor?: string | null;
      scent?: string | null;
      hardness?: string | null;
      transparency?: string | null;
    } | null;
    packaging?: {
      packQuantity?: number | null;
      packType?: string | null;
      packSize?: string | null;
    } | null;
    special?: {
      waterproof?: boolean | null;
      antibacterial?: boolean | null;
      organic?: boolean | null;
      recyclable?: boolean | null;
    } | null;
  } | null;
}

interface Category {
  id: string;
  name: string;
}

interface Brand {
  id: string;
  name: string;
}

interface Warehouse {
  id: string;
  name: string;
}

interface InventoryItemDetailModalProps {
  open: boolean;
  onClose: () => void;
  item: InventoryItem | null;
  categories: Category[];
  brands: Brand[];
  warehouses: Warehouse[];
  companyId: string | null;
  onItemUpdate?: (updatedItem: InventoryItem) => void;
}

// Para birimi sembolünü döndür
const getCurrencySymbol = (currency: string): string => {
  switch(currency) {
    case 'TRY': return '₺';
    case 'USD': return '$';
    case 'EUR': return '€';
    default: return currency;
  }
};

// KDV veya ÖTV durumunu metne çevir
const getTaxStatusText = (status: string): string => {
  return status === 'included' ? 'Dahil' : 'Hariç';
};

// Varyant değerini formatla
const formatVariantValue = (value: any, unit?: string): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Evet' : 'Hayır';
  if (typeof value === 'number') return unit ? `${value} ${unit}` : value.toString();
  return value.toString();
};

// Varyant kategorisinin var olup olmadığını kontrol et
const hasVariantData = (variants: any, category: string): boolean => {
  if (!variants || !variants[category]) return false;
  const categoryData = variants[category];
  return Object.values(categoryData).some(value => value !== null && value !== undefined && value !== '');
};

const InventoryItemDetailModal: React.FC<InventoryItemDetailModalProps> = ({
  open,
  onClose,
  item,
  categories,
  brands,
  warehouses,
  companyId,
  onItemUpdate
}) => {
  const { i18n } = useTranslation();
  // startsWith: "tr-TR" gibi bölgeli kodlarda da doğru çalışsın (bkz. src/i18n/index.ts)
  const isTr = !(i18n.language || 'tr').toLowerCase().startsWith('en');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const getTaxStatusLabel = (status?: string) => ((status || 'excluded') === 'included' ? tt('Dahil', 'Included') : tt('Hariç', 'Excluded'));
  const localizeUnit = (unit: string) => {
    if (isTr) return unit;
    const unitMap: Record<string, string> = {
      adet: 'pcs',
      çift: 'pair',
      paket: 'pack',
      kutu: 'box',
    };
    return unitMap[unit] || unit;
  };

  // Düzenleme modu için state
  const [isEditMode, setIsEditMode] = useState(false);
  // Düzenlenen veri için state
  const [editedItem, setEditedItem] = useState<InventoryItem | null>(null);
  // Kaydetme durumu
  const [isSaving, setIsSaving] = useState(false);
  // Hata mesajı
  const [error, setError] = useState<string | null>(null);
  // Resim önizleme modalı için state
  const [imagePreview, setImagePreview] = useState<{ open: boolean; url: string }>({ open: false, url: '' });
  
  // Varyant özellikleri için state'ler
  const [selectedVariantCategories, setSelectedVariantCategories] = useState<string[]>([]);
  
  // Fiziksel Özellikler
  const [variantColor, setVariantColor] = useState('');
  const [variantSize, setVariantSize] = useState('');
  const [variantLength, setVariantLength] = useState<number | ''>('');
  const [variantWidth, setVariantWidth] = useState<number | ''>('');
  const [variantHeight, setVariantHeight] = useState<number | ''>('');
  const [variantWeight, setVariantWeight] = useState<number | ''>('');
  const [variantThickness, setVariantThickness] = useState<number | ''>('');
  
  // Teknik Özellikler
  const [variantMaterial, setVariantMaterial] = useState('');
  const [variantGrade, setVariantGrade] = useState('');
  const [variantModel, setVariantModel] = useState('');
  const [variantSeries, setVariantSeries] = useState('');
  const [variantPower, setVariantPower] = useState<number | ''>('');
  const [variantVoltage, setVariantVoltage] = useState<number | ''>('');
  
  // Tasarım Özellikleri
  const [variantPattern, setVariantPattern] = useState('');
  const [variantStyle, setVariantStyle] = useState('');
  const [variantFinish, setVariantFinish] = useState('');
  const [variantShape, setVariantShape] = useState('');
  
  // Kapasite ve Boyut
  const [variantCapacity, setVariantCapacity] = useState<number | ''>('');
  const [variantDiameter, setVariantDiameter] = useState<number | ''>('');
  const [variantInnerSize, setVariantInnerSize] = useState('');
  const [variantOuterSize, setVariantOuterSize] = useState('');
  
  // Kullanım Özellikleri
  const [variantAgeGroup, setVariantAgeGroup] = useState('');
  const [variantGender, setVariantGender] = useState('');
  const [variantSeason, setVariantSeason] = useState('');
  const [variantUsage, setVariantUsage] = useState('');
  
  // Ürün Çeşitleri
  const [variantFlavor, setVariantFlavor] = useState('');
  const [variantScent, setVariantScent] = useState('');
  const [variantHardness, setVariantHardness] = useState('');
  const [variantTransparency, setVariantTransparency] = useState('');
  
  // Paketleme Özellikleri
  const [variantPackQuantity, setVariantPackQuantity] = useState<number | ''>('');
  const [variantPackType, setVariantPackType] = useState('');
  const [variantPackSize, setVariantPackSize] = useState('');
  
  // Özel Özellikler
  const [variantWaterproof, setVariantWaterproof] = useState<boolean | null>(null);
  const [variantAntibacterial, setVariantAntibacterial] = useState<boolean | null>(null);
  const [variantOrganic, setVariantOrganic] = useState<boolean | null>(null);
  const [variantRecyclable, setVariantRecyclable] = useState<boolean | null>(null);

  // Varyant kategori seçenekleri
  const variantCategoryOptions = [
    { value: 'physical', label: 'Fiziksel Özellikler' },
    { value: 'technical', label: 'Teknik Özellikler' },
    { value: 'design', label: 'Tasarım Özellikleri' },
    { value: 'capacity', label: 'Kapasite ve Boyut' },
    { value: 'usage', label: 'Kullanım Özellikleri' },
    { value: 'variety', label: 'Ürün Çeşitleri' },
    { value: 'packaging', label: 'Paketleme Özellikleri' },
    { value: 'special', label: 'Özel Özellikler' }
  ];

  // Varyant seçenek listleri
  const sizeOptions = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '28', '30', '32', '34', '36', '38', '40', '42', '44', '46', '48', '50'];
  const colorOptions = ['Kırmızı', 'Mavi', 'Yeşil', 'Sarı', 'Siyah', 'Beyaz', 'Gri', 'Mor', 'Turuncu', 'Pembe', 'Kahverengi', 'Lacivert'];
  const gradeOptions = ['A+', 'A', 'B+', 'B', 'C+', 'C', 'Birinci Sınıf', 'İkinci Sınıf', 'Üçüncü Sınıf'];
  const patternOptions = ['Düz', 'Çizgili', 'Kareli', 'Puantiyeli', 'Çiçekli', 'Geometrik', 'Desenli'];
  const styleOptions = ['Modern', 'Klasik', 'Vintage', 'Minimalist', 'Rustic', 'Endüstriyel'];
  const finishOptions = ['Mat', 'Parlak', 'Saten', 'Yağlı', 'Su Bazlı', 'Doğal'];
  const shapeOptions = ['Yuvarlak', 'Kare', 'Dikdörtgen', 'Oval', 'Üçgen', 'Altıgen'];
  const ageGroupOptions = ['Bebek (0-2)', 'Çocuk (3-12)', 'Genç (13-17)', 'Yetişkin (18-64)', 'Yaşlı (65+)'];
  const genderOptions = ['Erkek', 'Kadın', 'Unisex', 'Çocuk'];
  const seasonOptions = ['İlkbahar', 'Yaz', 'Sonbahar', 'Kış', 'Tüm Mevsim'];
  const usageOptions = ['İç Mekan', 'Dış Mekan', 'Profesyonel', 'Ev Kullanımı', 'Endüstriyel'];
  const hardnessOptions = ['Çok Yumuşak', 'Yumuşak', 'Orta', 'Sert', 'Çok Sert'];
  const transparencyOptions = ['Şeffaf', 'Yarı Şeffaf', 'Mat', 'Opak'];
  const packTypeOptions = ['Tekli', 'İkili', 'Üçlü', 'Set', 'Paket', 'Kutu'];
  const packSizeOptions = ['Mini', 'Küçük', 'Orta', 'Büyük', 'XL', 'XXL'];

  // Modal açıldığında veya item değiştiğinde editedItem'ı güncelle
  useEffect(() => {
    if (item) {
      setEditedItem({ ...item });
      loadVariantData(item);
    }
  }, [item]);

  // NOT: Real-time listener kaldırıldı (offline mod için)
  // Veriler props üzerinden gelir, güncelleme sonrası onItemUpdate callback'i kullanılır

  // Mevcut varyant verilerini state'lere yükle
  const loadVariantData = (item: InventoryItem) => {
    if (!item.variants) {
      clearVariantData();
      return;
    }

    const activeCategories: string[] = [];

    // Fiziksel Özellikler
    if (item.variants.physical) {
      activeCategories.push('physical');
      setVariantColor(item.variants.physical.color || '');
      setVariantSize(item.variants.physical.size || '');
      setVariantLength(item.variants.physical.length || '');
      setVariantWidth(item.variants.physical.width || '');
      setVariantHeight(item.variants.physical.height || '');
      setVariantWeight(item.variants.physical.weight || '');
      setVariantThickness(item.variants.physical.thickness || '');
    }

    // Teknik Özellikler
    if (item.variants.technical) {
      activeCategories.push('technical');
      setVariantMaterial(item.variants.technical.material || '');
      setVariantGrade(item.variants.technical.grade || '');
      setVariantModel(item.variants.technical.model || '');
      setVariantSeries(item.variants.technical.series || '');
      setVariantPower(item.variants.technical.power || '');
      setVariantVoltage(item.variants.technical.voltage || '');
    }

    // Tasarım Özellikleri
    if (item.variants.design) {
      activeCategories.push('design');
      setVariantPattern(item.variants.design.pattern || '');
      setVariantStyle(item.variants.design.style || '');
      setVariantFinish(item.variants.design.finish || '');
      setVariantShape(item.variants.design.shape || '');
    }

    // Kapasite ve Boyut
    if (item.variants.capacity) {
      activeCategories.push('capacity');
      setVariantCapacity(item.variants.capacity.capacity || '');
      setVariantDiameter(item.variants.capacity.diameter || '');
      setVariantInnerSize(item.variants.capacity.innerSize || '');
      setVariantOuterSize(item.variants.capacity.outerSize || '');
    }

    // Kullanım Özellikleri
    if (item.variants.usage) {
      activeCategories.push('usage');
      setVariantAgeGroup(item.variants.usage.ageGroup || '');
      setVariantGender(item.variants.usage.gender || '');
      setVariantSeason(item.variants.usage.season || '');
      setVariantUsage(item.variants.usage.usage || '');
    }

    // Ürün Çeşitleri
    if (item.variants.variety) {
      activeCategories.push('variety');
      setVariantFlavor(item.variants.variety.flavor || '');
      setVariantScent(item.variants.variety.scent || '');
      setVariantHardness(item.variants.variety.hardness || '');
      setVariantTransparency(item.variants.variety.transparency || '');
    }

    // Paketleme Özellikleri
    if (item.variants.packaging) {
      activeCategories.push('packaging');
      setVariantPackQuantity(item.variants.packaging.packQuantity || '');
      setVariantPackType(item.variants.packaging.packType || '');
      setVariantPackSize(item.variants.packaging.packSize || '');
    }

    // Özel Özellikler
    if (item.variants.special) {
      activeCategories.push('special');
      setVariantWaterproof(item.variants.special.waterproof ?? null);
      setVariantAntibacterial(item.variants.special.antibacterial ?? null);
      setVariantOrganic(item.variants.special.organic ?? null);
      setVariantRecyclable(item.variants.special.recyclable ?? null);
    }

    setSelectedVariantCategories(activeCategories);
  };

  // Varyant verilerini sıfırla
  const clearVariantData = () => {
    setSelectedVariantCategories([]);
    setVariantColor('');
    setVariantSize('');
    setVariantLength('');
    setVariantWidth('');
    setVariantHeight('');
    setVariantWeight('');
    setVariantThickness('');
    setVariantMaterial('');
    setVariantGrade('');
    setVariantModel('');
    setVariantSeries('');
    setVariantPower('');
    setVariantVoltage('');
    setVariantPattern('');
    setVariantStyle('');
    setVariantFinish('');
    setVariantShape('');
    setVariantCapacity('');
    setVariantDiameter('');
    setVariantInnerSize('');
    setVariantOuterSize('');
    setVariantAgeGroup('');
    setVariantGender('');
    setVariantSeason('');
    setVariantUsage('');
    setVariantFlavor('');
    setVariantScent('');
    setVariantHardness('');
    setVariantTransparency('');
    setVariantPackQuantity('');
    setVariantPackType('');
    setVariantPackSize('');
    setVariantWaterproof(null);
    setVariantAntibacterial(null);
    setVariantOrganic(null);
    setVariantRecyclable(null);
  };

  if (!item || !editedItem) return null;

  // Input içeriğini otomatik seç
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  // Resim silme
  const handleDeleteImage = (urlToDelete: string) => {
    if (!editedItem || !editedItem.imageUrl) return;

    const updatedImageUrls = editedItem.imageUrl.split(',').filter(url => url !== urlToDelete);
    const newImageUrl = updatedImageUrls.join(',');

    setEditedItem({
      ...editedItem,
      imageUrl: newImageUrl || null,
    });
  };

  // Resim önizleme modalını aç/kapat
  const handleOpenImagePreview = (url: string) => {
    setImagePreview({ open: true, url });
  };

  const handleCloseImagePreview = () => {
    setImagePreview({ open: false, url: '' });
  };

  // Düzenleme modunu aç/kapat
  const toggleEditMode = () => {
    if (isEditMode) {
      // Düzenleme modundan çıkarken değişiklikleri iptal et
      setEditedItem({ ...item });
      loadVariantData(item); // Varyant verilerini de sıfırla
    }
    setIsEditMode(!isEditMode);
    setError(null);
  };

  // Input değişikliklerini yakala
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditedItem({
      ...editedItem,
      [name]: value
    });
  };

  // Sayısal değerleri yakala
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditedItem({
      ...editedItem,
      [name]: value === '' ? 0 : Number(value)
    });
  };

  // Select değişikliklerini yakala
  const handleSelectChange = (name: string, value: any) => {
    setEditedItem({
      ...editedItem,
      [name]: value
    });
  };

  // Switch değişikliklerini yakala
  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setEditedItem({
      ...editedItem,
      [name]: checked
    });
  };

  // Kategori ve Marka değişikliklerini yakala
  const handleRelationChange = (name: string, value: any) => {
    setEditedItem({
      ...editedItem,
      [name]: value ? value.id : null
    });
  };

  // Verileri kaydet
  const handleSave = async () => {
    if (!editedItem || !companyId) return;

    setIsSaving(true);
    setError(null);

    try {
      console.log('💾 [SAVE ITEM] Ürün güncelleniyor...');
      console.log('📋 [SAVE ITEM] editedItem:', editedItem);
      
      // Varyant verilerini editedItem'a ekle
      const variants = {
        physical: selectedVariantCategories.includes('physical') ? {
          color: variantColor || null,
          size: variantSize || null,
          length: variantLength === '' ? null : Number(variantLength),
          width: variantWidth === '' ? null : Number(variantWidth),
          height: variantHeight === '' ? null : Number(variantHeight),
          weight: variantWeight === '' ? null : Number(variantWeight),
          thickness: variantThickness === '' ? null : Number(variantThickness)
        } : null,
        technical: selectedVariantCategories.includes('technical') ? {
          material: variantMaterial || null,
          grade: variantGrade || null,
          model: variantModel || null,
          series: variantSeries || null,
          power: variantPower === '' ? null : Number(variantPower),
          voltage: variantVoltage === '' ? null : Number(variantVoltage)
        } : null,
        design: selectedVariantCategories.includes('design') ? {
          pattern: variantPattern || null,
          style: variantStyle || null,
          finish: variantFinish || null,
          shape: variantShape || null
        } : null,
        capacity: selectedVariantCategories.includes('capacity') ? {
          capacity: variantCapacity === '' ? null : Number(variantCapacity),
          diameter: variantDiameter === '' ? null : Number(variantDiameter),
          innerSize: variantInnerSize || null,
          outerSize: variantOuterSize || null
        } : null,
        usage: selectedVariantCategories.includes('usage') ? {
          ageGroup: variantAgeGroup || null,
          gender: variantGender || null,
          season: variantSeason || null,
          usage: variantUsage || null
        } : null,
        variety: selectedVariantCategories.includes('variety') ? {
          flavor: variantFlavor || null,
          scent: variantScent || null,
          hardness: variantHardness || null,
          transparency: variantTransparency || null
        } : null,
        packaging: selectedVariantCategories.includes('packaging') ? {
          packQuantity: variantPackQuantity === '' ? null : Number(variantPackQuantity),
          packType: variantPackType || null,
          packSize: variantPackSize || null
        } : null,
        special: selectedVariantCategories.includes('special') ? {
          waterproof: variantWaterproof,
          antibacterial: variantAntibacterial,
          organic: variantOrganic,
          recyclable: variantRecyclable
        } : null
      };

      // SQL için DTO oluştur
      const { InventoryService } = await import('./services/inventoryService');
      
      const updateDto: any = {
        id: editedItem.id,
        companyId: companyId,
        stockCode: editedItem.stockCode,
        name: editedItem.name,
        categoryId: editedItem.categoryId || null,
        brandId: editedItem.brandId || null,
        // ⚠️ DEPRECATED: warehouseId ve quantity artık WarehouseStock'ta yönetiliyor
        branchesId: (editedItem as any).branchesId || null,
        // Fiyatlar
        purchasePrice: editedItem.purchasePrice || 0,
        purchaseCurrency: editedItem.purchaseCurrency || 'TRY',
        salePrice: editedItem.salePrice,
        saleCurrency: editedItem.saleCurrency || 'TRY',
        listPrice: editedItem.listPrice || null,
        listCurrency: editedItem.listCurrency || null,
        // Stok - ⚠️ TODO: Depo bazlı stok yönetimi için ayrı endpoint gerekli
        unit: editedItem.unit || 'adet',
        canBeNegative: editedItem.canBeNegative || false,
        minStockLevel: (editedItem as any).minStockLevel || null,
        maxStockLevel: (editedItem as any).maxStockLevel || null,
        description: editedItem.description || '',
        barcode: editedItem.barcode || '',
        // Vergiler
        vatRate: editedItem.vatRate || 20,
        vatStatus: editedItem.vatStatus || 'included',
        sctRate: editedItem.sctRate || null,
        sctStatus: editedItem.sctStatus || null,
        // Diğer
        imageUrl: editedItem.imageUrl || null,
        imageUrls: (editedItem as any).imageUrls 
          ? (Array.isArray((editedItem as any).imageUrls) 
              ? (editedItem as any).imageUrls 
              : [(editedItem as any).imageUrls])
          : null,
        variants: null, // Backend bunu kullanmıyor, null gönder
        printerGroup: (editedItem as any).printerGroup || null,
        departmentNo: editedItem.departmentNo || null,
        isActive: editedItem.isActive
      };

      console.log('📤 [SAVE ITEM] SQL DTO:', updateDto);
      
      // SQL API ile güncelle
      const updatedItem = await InventoryService.updateItem(companyId!, editedItem.id, updateDto);
      
      console.log('✅ [SAVE ITEM] Stok başarıyla güncellendi:', updatedItem);
      
      // Parent component'e bildir
      if (onItemUpdate) {
        onItemUpdate({
          ...editedItem,
          ...updatedItem
        } as any);
      }
      
      // Düzenleme modundan çık
      setIsEditMode(false);
      setIsSaving(false);
      
    } catch (error: any) {
      console.error('❌ [SAVE ITEM] Stok güncellenirken hata oluştu:', error);
      console.error('❌ [SAVE ITEM] Hata detayı:', error.response?.data);
      console.error('❌ [SAVE ITEM] Hata status:', error.response?.status);
      
      const errorMessage = error.response?.data?.errors 
        ? Object.values(error.response.data.errors).flat().join(', ')
        : error.response?.data?.message || 'Stok güncellenirken bir hata oluştu. Lütfen tekrar deneyin.';
      
      setError(errorMessage);
      setIsSaving(false);
    }
  };

  // Kategori adını bul
  const getCategoryName = (categoryId: string | null): string => {
    if (!categoryId) return '-';
    const category = categories.find(c => c.id === categoryId);
    if (!category) return '-';
    return category.name;
  };

  // Marka adını bul
  const getBrandName = (brandId: string | null): string => {
    if (!brandId) return '-';
    const brand = brands.find(b => b.id === brandId);
    if (!brand) return '-';
    return brand.name;
  };

  // Depo adını bul
  const getWarehouseName = (warehouseId: string | null): string => {
    if (!warehouseId) return '-';
    const warehouse = warehouses.find(w => w.id === warehouseId);
    if (!warehouse) return '-';
    return warehouse.name;
  };

  // Kategori objesini bul
  const getCategoryObject = (categoryId: string | null): Category | null => {
    if (!categoryId) return null;
    return categories.find(c => c.id === categoryId) || null;
  };

  // Marka objesini bul
  const getBrandObject = (brandId: string | null): Brand | null => {
    if (!brandId) return null;
    return brands.find(b => b.id === brandId) || null;
  };

  // Depo objesini bul
  const getWarehouseObject = (warehouseId: string | null): Warehouse | null => {
    if (!warehouseId) return null;
    return warehouses.find(w => w.id === warehouseId) || null;
  };

  // Item henüz yüklenmemişse gösterme
  if (!item) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={!isEditMode && !isSaving ? onClose : undefined}
      maxWidth="md"
      fullWidth
      aria-labelledby="inventory-item-detail-title"
    >
      <DialogTitle id="inventory-item-detail-title" sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <InventoryIcon sx={{ mr: 1 }} />
          <Typography variant="h6" component="div">
            {/* ✅ Sadece görüntüleme modu */}
            {tt('Stok Ayrıntıları', 'Stock Details')}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
            ID: {item.id}
          </Typography>
        </Box>
        <Box>
          {/* ✅ Düzenleme butonu kaldırıldı - artık sadece görüntüleme modu */}
          <IconButton
            aria-label="kapat"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ pb: 3 }}>
        {error && (
          <Box sx={{ mb: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          {isEditMode ? (
            <TextField
              fullWidth
              label={tt('Ürün Adı', 'Product Name')}
              name="name"
              value={editedItem.name}
              onChange={handleInputChange}
              margin="normal"
              required
              variant="outlined"
              onFocus={handleInputFocus}
            />
          ) : (
            <Typography variant="h5" component="h2" gutterBottom fontWeight="bold">
              {item.name}
            </Typography>
          )}
          
          {isEditMode ? (
            <FormControlLabel
              control={
                <Switch
                  checked={editedItem.isActive}
                  onChange={handleSwitchChange}
                  name="isActive"
                  color="success"
                />
              }
              label={tt('Aktif', 'Active')}
            />
          ) : (
            <Chip 
              label={item.isActive ? tt('Aktif', 'Active') : tt('Pasif', 'Passive')} 
              color={item.isActive ? "success" : "default"} 
              size="small" 
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>

        <Grid container spacing={3}>
          {/* Temel Bilgiler */}
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <QrCodeIcon sx={{ mr: 1 }} />
                {tt('Temel Bilgiler', 'Basic Information')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {isEditMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={tt('Stok Kodu', 'Stock Code')}
                      name="stockCode"
                      value={editedItem.stockCode}
                      onChange={handleInputChange}
                      size="small"
                      required
                      onFocus={handleInputFocus}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={tt('Barkod', 'Barcode')}
                      name="barcode"
                      value={editedItem.barcode || ''}
                      onChange={handleInputChange}
                      size="small"
                      onFocus={handleInputFocus}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Autocomplete
                      options={categories}
                      getOptionLabel={(option) => option.name}
                      value={getCategoryObject(editedItem.categoryId || null)}
                      onChange={(_, newValue) => handleRelationChange('categoryId', newValue)}
                      renderInput={(params) => (
                        <TextField {...params} label={tt('Kategori', 'Category')} size="small" />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Autocomplete
                      options={brands}
                      getOptionLabel={(option) => option.name}
                      value={getBrandObject(editedItem.brandId || null)}
                      onChange={(_, newValue) => handleRelationChange('brandId', newValue)}
                      renderInput={(params) => (
                        <TextField {...params} label={tt('Marka', 'Brand')} size="small" />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Autocomplete
                      options={warehouses}
                      getOptionLabel={(option) => option.name}
                      value={getWarehouseObject((editedItem as any).warehouseId || null)}
                      onChange={(_, newValue) => handleRelationChange('warehouseId', newValue)}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label={tt('Depo (Salt Okunur - WarehouseStock ile yönetiliyor)', 'Warehouse (Read-only - managed via WarehouseStock)')}
                          size="small" 
                          disabled
                          helperText={tt('Depo ataması artık WarehouseStock tablosu ile yönetiliyor', 'Warehouse assignment is now managed by WarehouseStock table')}
                        />
                      )}
                      disabled
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">{tt('Stok Kodu', 'Stock Code')}:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2">{item.stockCode || '-'}</Typography>
                  </Grid>
                  
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">{tt('Barkod', 'Barcode')}:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2">{item.barcode || '-'}</Typography>
                  </Grid>
                  
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">{tt('Kategori', 'Category')}:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2">{getCategoryName(item.categoryId || null)}</Typography>
                  </Grid>
                  
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">{tt('Marka', 'Brand')}:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2">{getBrandName(item.brandId || null)}</Typography>
                  </Grid>
                  
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">{tt('Depo', 'Warehouse')}:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2">
                      {/* ⚠️ TODO: Depo bilgisi artık WarehouseStock'tan gelecek */}
                      {item.warehouseStocks && item.warehouseStocks.length > 0
                        ? item.warehouseStocks.map(ws => ws.warehouseName || ws.warehouseId).join(', ')
                        : '-'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">{tt('Oluşturulma Tarihi', 'Created Date')}:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2">
                      {new Date(item.createdAt).toLocaleDateString(localeCode)}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Paper>
          </Grid>
          
          {/* Miktar ve Fiyat Bilgileri */}
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <LocalAtmIcon sx={{ mr: 1 }} />
                {tt('Miktar ve Fiyat Bilgileri', 'Quantity and Price Information')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {isEditMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={tt('Stok Miktarı (Salt Okunur - WarehouseStock ile yönetiliyor)', 'Stock Quantity (Read-only - managed via WarehouseStock)')}
                      name="quantity"
                      type="number"
                      value={editedItem.warehouseStocks?.reduce((sum, ws) => sum + ws.quantity, 0) || 0}
                      size="small"
                      disabled
                      helperText={tt('Stok miktarı artık depo bazlı WarehouseStock tablosu ile yönetiliyor', 'Stock quantity is now managed per warehouse in WarehouseStock table')}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <TextField
                              select
                              name="unit"
                              value={editedItem.unit}
                              onChange={handleInputChange}
                              variant="standard"
                              size="small"
                              sx={{ width: 80 }}
                            >
                              <MenuItem value="adet">{tt('Adet', 'Piece')}</MenuItem>
                              <MenuItem value="kg">Kg</MenuItem>
                              <MenuItem value="lt">Lt</MenuItem>
                              <MenuItem value="mt">Mt</MenuItem>
                              <MenuItem value="m2">M²</MenuItem>
                              <MenuItem value="m3">M³</MenuItem>
                              <MenuItem value="çift">{tt('Çift', 'Pair')}</MenuItem>
                              <MenuItem value="paket">{tt('Paket', 'Package')}</MenuItem>
                              <MenuItem value="kutu">{tt('Kutu', 'Box')}</MenuItem>
                            </TextField>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={tt('Alış Fiyatı', 'Purchase Price')}
                      name="purchasePrice"
                      type="number"
                      value={editedItem.purchasePrice}
                      onChange={handleNumberChange}
                      size="small"
                      required
                      onFocus={handleInputFocus}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <TextField
                              select
                              name="purchaseCurrency"
                              value={editedItem.purchaseCurrency || 'TRY'}
                              onChange={handleInputChange}
                              variant="standard"
                              size="small"
                              sx={{ width: 60 }}
                            >
                              <MenuItem value="TRY">₺</MenuItem>
                              <MenuItem value="USD">$</MenuItem>
                              <MenuItem value="EUR">€</MenuItem>
                            </TextField>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={tt('Satış Fiyatı', 'Sale Price')}
                      name="salePrice"
                      type="number"
                      value={editedItem.salePrice}
                      onChange={handleNumberChange}
                      size="small"
                      required
                      onFocus={handleInputFocus}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <TextField
                              select
                              name="saleCurrency"
                              value={editedItem.saleCurrency || 'TRY'}
                              onChange={handleInputChange}
                              variant="standard"
                              size="small"
                              sx={{ width: 60 }}
                            >
                              <MenuItem value="TRY">₺</MenuItem>
                              <MenuItem value="USD">$</MenuItem>
                              <MenuItem value="EUR">€</MenuItem>
                            </TextField>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={tt('Liste Fiyatı', 'List Price')}
                      name="listPrice"
                      type="number"
                      value={editedItem.listPrice || 0}
                      onChange={handleNumberChange}
                      size="small"
                      onFocus={handleInputFocus}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <TextField
                              select
                              name="listCurrency"
                              value={editedItem.listCurrency || 'TRY'}
                              onChange={handleInputChange}
                              variant="standard"
                              size="small"
                              sx={{ width: 60 }}
                            >
                              <MenuItem value="TRY">₺</MenuItem>
                              <MenuItem value="USD">$</MenuItem>
                              <MenuItem value="EUR">€</MenuItem>
                            </TextField>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editedItem.canBeNegative || false}
                          onChange={handleSwitchChange}
                          name="canBeNegative"
                          size="small"
                        />
                      }
                      label={tt('Stok eksiye düşebilir', 'Stock can go negative')}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">{tt('Stok Miktarı', 'Stock Quantity')}:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2" fontWeight="bold">
                      {/* ✅ YENİ SİSTEM: WarehouseStocks'tan toplam hesapla */}
                      {(item.warehouseStocks?.reduce((sum, ws) => sum + ws.quantity, 0) || 0).toLocaleString(localeCode)} {localizeUnit(item.unit)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">{tt('Alış Fiyatı', 'Purchase Price')}:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2">
                      {(item.purchasePrice || 0).toLocaleString(localeCode)} {getCurrencySymbol(item.purchaseCurrency || 'TRY')}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">{tt('Satış Fiyatı', 'Sale Price')}:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2" fontWeight="bold">
                      {(item.salePrice || 0).toLocaleString(localeCode)} {getCurrencySymbol(item.saleCurrency || 'TRY')}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">{tt('Liste Fiyatı', 'List Price')}:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2">
                      {item.listPrice ? `${(item.listPrice || 0).toLocaleString(localeCode)} ${getCurrencySymbol(item.listCurrency || 'TRY')}` : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">{tt('Stok Eksiye Düşebilir', 'Stock Can Go Negative')}:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2">
                      {item.canBeNegative ? tt('Evet', 'Yes') : tt('Hayır', 'No')}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Paper>
          </Grid>
          
          {/* Lokasyon Dağılımı (akıllı depo stokları varsa) */}
          {item.warehouseStocks && item.warehouseStocks.some((ws: any) => ws.locationCode) && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {tt('Lokasyon Dağılımı', 'Location Distribution')}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #ddd' }}>{tt('Lokasyon', 'Location')}</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #ddd' }}>{tt('Depo', 'Warehouse')}</th>
                        <th style={{ textAlign: 'right', padding: '4px 8px', borderBottom: '1px solid #ddd' }}>{tt('Miktar', 'Quantity')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.warehouseStocks.filter((ws: any) => ws.locationCode).map((ws: any, idx: number) => (
                        <tr key={idx}>
                          <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontWeight: 600, fontSize: '0.8rem' }}>
                            {ws.locationCode}
                            {ws.locationFullPath && (
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                {ws.locationFullPath}
                              </Typography>
                            )}
                          </td>
                          <td style={{ padding: '4px 8px' }}>{ws.warehouseName || '-'}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600 }}>{ws.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Paper>
            </Grid>
          )}
          
          {/* Vergi Bilgileri */}
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <PercentIcon sx={{ mr: 1 }} />
                {tt('Vergi Bilgileri', 'Tax Information')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {isEditMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Grid container spacing={1}>
                      <Grid item xs={8}>
                        <TextField
                          fullWidth
                          label={tt('KDV Oranı', 'VAT Rate')}
                          name="vatRate"
                          type="number"
                          value={editedItem.vatRate || 0}
                          onChange={handleNumberChange}
                          size="small"
                          onFocus={handleInputFocus}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">%</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          select
                          fullWidth
                          label={tt('Durumu', 'Status')}
                          name="vatStatus"
                          value={editedItem.vatStatus || 'excluded'}
                          onChange={handleInputChange}
                          size="small"
                        >
                          <MenuItem value="included">{tt('Dahil', 'Included')}</MenuItem>
                          <MenuItem value="excluded">{tt('Hariç', 'Excluded')}</MenuItem>
                        </TextField>
                      </Grid>
                    </Grid>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Grid container spacing={1}>
                      <Grid item xs={8}>
                        <TextField
                          fullWidth
                          label={tt('ÖTV Oranı', 'SCT Rate')}
                          name="sctRate"
                          type="number"
                          value={editedItem.sctRate || 0}
                          onChange={handleNumberChange}
                          size="small"
                          onFocus={handleInputFocus}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">%</InputAdornment>
                          }}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          select
                          fullWidth
                          label={tt('Durumu', 'Status')}
                          name="sctStatus"
                          value={editedItem.sctStatus || 'excluded'}
                          onChange={handleInputChange}
                          size="small"
                        >
                          <MenuItem value="included">{tt('Dahil', 'Included')}</MenuItem>
                          <MenuItem value="excluded">{tt('Hariç', 'Excluded')}</MenuItem>
                        </TextField>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">{tt('KDV Oranı', 'VAT Rate')}:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2">%{item.vatRate || 0}</Typography>
                  </Grid>
                  
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">{tt('KDV Durumu', 'VAT Status')}:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2">{getTaxStatusLabel(item.vatStatus)}</Typography>
                  </Grid>
                  
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">{tt('ÖTV Oranı', 'SCT Rate')}:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2">{item.sctRate ? `%${item.sctRate}` : '-'}</Typography>
                  </Grid>
                  
                  <Grid item xs={5}>
                    <Typography variant="body2" color="text.secondary">{tt('ÖTV Durumu', 'SCT Status')}:</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2">
                      {item.sctRate ? getTaxStatusLabel(item.sctStatus) : '-'}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Paper>
          </Grid>
          
          {/* Açıklama */}
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <DescriptionIcon sx={{ mr: 1 }} />
                {tt('Açıklama', 'Description')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {isEditMode ? (
                <ProductDescriptionEditor
                  key={item.id}
                  value={editedItem.description || ''}
                  onChange={(html) => setEditedItem({ ...editedItem, description: html })}
                />
              ) : isRichTextEmpty(item.description) ? (
                <Typography variant="body2">
                  {tt('Bu ürün için açıklama eklenmemiş.', 'No description added for this product.')}
                </Typography>
              ) : (
                <Box
                  sx={{
                    '& p': { mb: 1 },
                    '& h2': { fontSize: '1.1rem', fontWeight: 700, mt: 1.5, mb: 1 },
                    '& h3': { fontSize: '1rem', fontWeight: 600, mt: 1.25, mb: 0.75 },
                    '& ul, & ol': { pl: 3, mb: 1 },
                    '& li': { mb: 0.5 },
                    '& a': { color: 'primary.main' },
                  }}
                  dangerouslySetInnerHTML={{ __html: sanitizeRichText(item.description) }}
                />
              )}
            </Paper>
          </Grid>
          
          {/* Departman No (ÖKC) */}
          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <CategoryIcon sx={{ mr: 1 }} />
                Departman No (ÖKC)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {isEditMode ? (
                <>
                  <TextField
                    fullWidth
                    label="Departman No (ÖKC)"
                    name="departmentNo"
                    type="number"
                    value={editedItem.departmentNo || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditedItem({
                        ...editedItem,
                        departmentNo: value === '' ? null : (Number(value) >= 1 && Number(value) <= 10 ? Number(value) : editedItem.departmentNo),
                      });
                    }}
                    size="small"
                    onFocus={handleInputFocus}
                    InputProps={{
                      inputProps: { 
                        min: 1, 
                        max: 10,
                        step: 1 
                      }
                    }}
                    helperText="1-10 arası bir sayı giriniz."
                  />
                  <Typography variant="caption" display="block" color="textSecondary" sx={{mt: 0.5}}>
                    Yazarkasa entegrasyonu kullanılacak ise mutlaka departman numarası girilmelidir. Lütfen ÖKC cihazınızın departman ayarlarını kontrol ediniz.
                  </Typography>
                </>
              ) : (
                <Typography variant="body2">
                  {item.departmentNo !== null && item.departmentNo !== undefined ? item.departmentNo : '-'}
                </Typography>
              )}
            </Paper>
          </Grid>
          
          {/* Varyant Özellikleri */}
          {(isEditMode || (item.variants && Object.values(item.variants).some(category => category !== null))) && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <CategoryIcon sx={{ mr: 1 }} />
                  Varyant Özellikleri {!isEditMode && '(Salt Okunur)'}
                  {isEditMode && <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>(Opsiyonel)</Typography>}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {isEditMode ? (
                  <Grid container spacing={2}>
                    {/* Varyant Kategori Seçimi */}
                    <Grid item xs={12}>
                      <Autocomplete
                        multiple
                        size="small"
                        options={variantCategoryOptions}
                        getOptionLabel={(option) => option.label}
                        value={variantCategoryOptions.filter(option => selectedVariantCategories.includes(option.value))}
                        onChange={(_, value) => setSelectedVariantCategories(value.map(v => v.value))}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Varyant Kategorileri Seçin"
                            placeholder="Özellik kategorilerini seçin..."
                            size="small"
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              variant="outlined"
                              label={option.label}
                              {...getTagProps({ index })}
                              size="small"
                            />
                          ))
                        }
                      />
                    </Grid>

                    {/* Fiziksel Özellikler */}
                    {selectedVariantCategories.includes('physical') && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mt: 2, mb: 1 }}>
                            Fiziksel Özellikler
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Renk</InputLabel>
                            <Select
                              value={variantColor}
                              onChange={(e) => setVariantColor(e.target.value)}
                              label="Renk"
                            >
                              {colorOptions.map(color => (
                                <MenuItem key={color} value={color}>{color}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Beden</InputLabel>
                            <Select
                              value={variantSize}
                              onChange={(e) => setVariantSize(e.target.value)}
                              label="Beden"
                            >
                              {sizeOptions.map(size => (
                                <MenuItem key={size} value={size}>{size}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Boy/Uzunluk"
                            value={variantLength}
                            onChange={(e) => setVariantLength(e.target.value === '' ? '' : Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">m</InputAdornment>
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Genişlik"
                            value={variantWidth}
                            onChange={(e) => setVariantWidth(e.target.value === '' ? '' : Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">cm</InputAdornment>
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Yükseklik"
                            value={variantHeight}
                            onChange={(e) => setVariantHeight(e.target.value === '' ? '' : Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">cm</InputAdornment>
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Ağırlık"
                            value={variantWeight}
                            onChange={(e) => setVariantWeight(e.target.value === '' ? '' : Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">kg</InputAdornment>
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Kalınlık"
                            value={variantThickness}
                            onChange={(e) => setVariantThickness(e.target.value === '' ? '' : Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">mm</InputAdornment>
                            }}
                          />
                        </Grid>
                      </>
                    )}

                    {/* Teknik Özellikler */}
                    {selectedVariantCategories.includes('technical') && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mt: 2, mb: 1 }}>
                            Teknik Özellikler
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Malzeme"
                            value={variantMaterial}
                            onChange={(e) => setVariantMaterial(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Kalite/Sınıf</InputLabel>
                            <Select
                              value={variantGrade}
                              onChange={(e) => setVariantGrade(e.target.value)}
                              label="Kalite/Sınıf"
                            >
                              {gradeOptions.map(grade => (
                                <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Model"
                            value={variantModel}
                            onChange={(e) => setVariantModel(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Seri"
                            value={variantSeries}
                            onChange={(e) => setVariantSeries(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Güç"
                            value={variantPower}
                            onChange={(e) => setVariantPower(e.target.value === '' ? '' : Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">W</InputAdornment>
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Voltaj"
                            value={variantVoltage}
                            onChange={(e) => setVariantVoltage(e.target.value === '' ? '' : Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">V</InputAdornment>
                            }}
                          />
                        </Grid>
                      </>
                    )}

                    {/* Tasarım Özellikleri */}
                    {selectedVariantCategories.includes('design') && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mt: 2, mb: 1 }}>
                            Tasarım Özellikleri
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Desen</InputLabel>
                            <Select
                              value={variantPattern}
                              onChange={(e) => setVariantPattern(e.target.value)}
                              label="Desen"
                            >
                              {patternOptions.map(pattern => (
                                <MenuItem key={pattern} value={pattern}>{pattern}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Stil</InputLabel>
                            <Select
                              value={variantStyle}
                              onChange={(e) => setVariantStyle(e.target.value)}
                              label="Stil"
                            >
                              {styleOptions.map(style => (
                                <MenuItem key={style} value={style}>{style}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Finish/Kaplama</InputLabel>
                            <Select
                              value={variantFinish}
                              onChange={(e) => setVariantFinish(e.target.value)}
                              label="Finish/Kaplama"
                            >
                              {finishOptions.map(finish => (
                                <MenuItem key={finish} value={finish}>{finish}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Şekil</InputLabel>
                            <Select
                              value={variantShape}
                              onChange={(e) => setVariantShape(e.target.value)}
                              label="Şekil"
                            >
                              {shapeOptions.map(shape => (
                                <MenuItem key={shape} value={shape}>{shape}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    )}

                    {/* Kapasite ve Boyut */}
                    {selectedVariantCategories.includes('capacity') && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mt: 2, mb: 1 }}>
                            Kapasite ve Boyut
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Kapasite"
                            value={variantCapacity}
                            onChange={(e) => setVariantCapacity(e.target.value === '' ? '' : Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">L</InputAdornment>
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Çap"
                            value={variantDiameter}
                            onChange={(e) => setVariantDiameter(e.target.value === '' ? '' : Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">cm</InputAdornment>
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="İç Boyut"
                            value={variantInnerSize}
                            onChange={(e) => setVariantInnerSize(e.target.value)}
                            placeholder="ör: 20x30x40 cm"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Dış Boyut"
                            value={variantOuterSize}
                            onChange={(e) => setVariantOuterSize(e.target.value)}
                            placeholder="ör: 25x35x45 cm"
                          />
                        </Grid>
                      </>
                    )}

                    {/* Kullanım Özellikleri */}
                    {selectedVariantCategories.includes('usage') && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mt: 2, mb: 1 }}>
                            Kullanım Özellikleri
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Yaş Grubu</InputLabel>
                            <Select
                              value={variantAgeGroup}
                              onChange={(e) => setVariantAgeGroup(e.target.value)}
                              label="Yaş Grubu"
                            >
                              {ageGroupOptions.map(age => (
                                <MenuItem key={age} value={age}>{age}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Cinsiyet</InputLabel>
                            <Select
                              value={variantGender}
                              onChange={(e) => setVariantGender(e.target.value)}
                              label="Cinsiyet"
                            >
                              {genderOptions.map(gender => (
                                <MenuItem key={gender} value={gender}>{gender}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Mevsim</InputLabel>
                            <Select
                              value={variantSeason}
                              onChange={(e) => setVariantSeason(e.target.value)}
                              label="Mevsim"
                            >
                              {seasonOptions.map(season => (
                                <MenuItem key={season} value={season}>{season}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Kullanım Amacı</InputLabel>
                            <Select
                              value={variantUsage}
                              onChange={(e) => setVariantUsage(e.target.value)}
                              label="Kullanım Amacı"
                            >
                              {usageOptions.map(usage => (
                                <MenuItem key={usage} value={usage}>{usage}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    )}

                    {/* Ürün Çeşitleri */}
                    {selectedVariantCategories.includes('variety') && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mt: 2, mb: 1 }}>
                            Ürün Çeşitleri
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Aroma/Tat"
                            value={variantFlavor}
                            onChange={(e) => setVariantFlavor(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Parfüm/Koku"
                            value={variantScent}
                            onChange={(e) => setVariantScent(e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Sertlik</InputLabel>
                            <Select
                              value={variantHardness}
                              onChange={(e) => setVariantHardness(e.target.value)}
                              label="Sertlik"
                            >
                              {hardnessOptions.map(hardness => (
                                <MenuItem key={hardness} value={hardness}>{hardness}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Şeffaflık</InputLabel>
                            <Select
                              value={variantTransparency}
                              onChange={(e) => setVariantTransparency(e.target.value)}
                              label="Şeffaflık"
                            >
                              {transparencyOptions.map(transparency => (
                                <MenuItem key={transparency} value={transparency}>{transparency}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    )}

                    {/* Paketleme Özellikleri */}
                    {selectedVariantCategories.includes('packaging') && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mt: 2, mb: 1 }}>
                            Paketleme Özellikleri
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Paket Adedi"
                            value={variantPackQuantity}
                            onChange={(e) => setVariantPackQuantity(e.target.value === '' ? '' : Number(e.target.value))}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">adet</InputAdornment>
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Paket Tipi</InputLabel>
                            <Select
                              value={variantPackType}
                              onChange={(e) => setVariantPackType(e.target.value)}
                              label="Paket Tipi"
                            >
                              {packTypeOptions.map(type => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Paket Boyutu</InputLabel>
                            <Select
                              value={variantPackSize}
                              onChange={(e) => setVariantPackSize(e.target.value)}
                              label="Paket Boyutu"
                            >
                              {packSizeOptions.map(size => (
                                <MenuItem key={size} value={size}>{size}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    )}

                    {/* Özel Özellikler */}
                    {selectedVariantCategories.includes('special') && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="body2" fontWeight="bold" color="primary" sx={{ mt: 2, mb: 1 }}>
                            Özel Özellikler
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={variantWaterproof === true}
                                onChange={(e) => setVariantWaterproof(e.target.checked ? true : null)}
                              />
                            }
                            label="Su Geçirmez"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={variantAntibacterial === true}
                                onChange={(e) => setVariantAntibacterial(e.target.checked ? true : null)}
                              />
                            }
                            label="Antibakteriyel"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={variantOrganic === true}
                                onChange={(e) => setVariantOrganic(e.target.checked ? true : null)}
                              />
                            }
                            label="Organik"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={variantRecyclable === true}
                                onChange={(e) => setVariantRecyclable(e.target.checked ? true : null)}
                              />
                            }
                            label="Geri Dönüştürülebilir"
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                ) : (
                  <Grid container spacing={3}>
                  {/* Fiziksel Özellikler */}
                  {item.variants && hasVariantData(item.variants, 'physical') && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold" color="primary" gutterBottom>
                          Fiziksel Özellikler
                        </Typography>
                        <Grid container spacing={1}>
                          {item.variants.physical?.color && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Renk:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.physical.color)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.physical?.size && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Beden:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.physical.size)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.physical?.length && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Boy/Uzunluk:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.physical.length, 'm')}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.physical?.width && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Genişlik:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.physical.width, 'cm')}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.physical?.height && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Yükseklik:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.physical.height, 'cm')}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.physical?.weight && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Ağırlık:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.physical.weight, 'kg')}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.physical?.thickness && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Kalınlık:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.physical.thickness, 'mm')}</Typography>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                  )}

                  {/* Teknik Özellikler */}
                  {item.variants && hasVariantData(item.variants, 'technical') && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold" color="primary" gutterBottom>
                          Teknik Özellikler
                        </Typography>
                        <Grid container spacing={1}>
                          {item.variants.technical?.material && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Malzeme:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.technical.material)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.technical?.grade && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Kalite/Sınıf:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.technical.grade)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.technical?.model && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Model:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.technical.model)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.technical?.series && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Seri:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.technical.series)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.technical?.power && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Güç:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.technical.power, 'W')}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.technical?.voltage && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Voltaj:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.technical.voltage, 'V')}</Typography>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                  )}

                  {/* Tasarım Özellikleri */}
                  {item.variants && hasVariantData(item.variants, 'design') && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold" color="primary" gutterBottom>
                          Tasarım Özellikleri
                        </Typography>
                        <Grid container spacing={1}>
                          {item.variants.design?.pattern && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Desen:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.design.pattern)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.design?.style && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Stil:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.design.style)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.design?.finish && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Finish/Kaplama:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.design.finish)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.design?.shape && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Şekil:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.design.shape)}</Typography>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                  )}

                  {/* Kapasite ve Boyut */}
                  {item.variants && hasVariantData(item.variants, 'capacity') && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold" color="primary" gutterBottom>
                          Kapasite ve Boyut
                        </Typography>
                        <Grid container spacing={1}>
                          {item.variants.capacity?.capacity && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Kapasite:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.capacity.capacity, 'L')}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.capacity?.diameter && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Çap:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.capacity.diameter, 'cm')}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.capacity?.innerSize && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">İç Boyut:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.capacity.innerSize)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.capacity?.outerSize && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Dış Boyut:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.capacity.outerSize)}</Typography>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                  )}

                  {/* Kullanım Özellikleri */}
                  {item.variants && hasVariantData(item.variants, 'usage') && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold" color="primary" gutterBottom>
                          Kullanım Özellikleri
                        </Typography>
                        <Grid container spacing={1}>
                          {item.variants.usage?.ageGroup && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Yaş Grubu:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.usage.ageGroup)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.usage?.gender && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Cinsiyet:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.usage.gender)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.usage?.season && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Mevsim:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.usage.season)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.usage?.usage && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Kullanım Amacı:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.usage.usage)}</Typography>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                  )}

                  {/* Ürün Çeşitleri */}
                  {item.variants && hasVariantData(item.variants, 'variety') && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold" color="primary" gutterBottom>
                          Ürün Çeşitleri
                        </Typography>
                        <Grid container spacing={1}>
                          {item.variants.variety?.flavor && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Aroma/Tat:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.variety.flavor)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.variety?.scent && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Parfüm/Koku:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.variety.scent)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.variety?.hardness && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Sertlik:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.variety.hardness)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.variety?.transparency && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Şeffaflık:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.variety.transparency)}</Typography>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                  )}

                  {/* Paketleme Özellikleri */}
                  {item.variants && hasVariantData(item.variants, 'packaging') && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold" color="primary" gutterBottom>
                          Paketleme Özellikleri
                        </Typography>
                        <Grid container spacing={1}>
                          {item.variants.packaging?.packQuantity && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Paket Adedi:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.packaging.packQuantity, 'adet')}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.packaging?.packType && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Paket Tipi:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.packaging.packType)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.packaging?.packSize && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Paket Boyutu:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.packaging.packSize)}</Typography>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                  )}

                  {/* Özel Özellikler */}
                  {item.variants && hasVariantData(item.variants, 'special') && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold" color="primary" gutterBottom>
                          Özel Özellikler
                        </Typography>
                        <Grid container spacing={1}>
                          {item.variants.special?.waterproof !== null && item.variants.special?.waterproof !== undefined && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Su Geçirmez:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.special.waterproof)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.special?.antibacterial !== null && item.variants.special?.antibacterial !== undefined && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Antibakteriyel:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.special.antibacterial)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.special?.organic !== null && item.variants.special?.organic !== undefined && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Organik:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.special.organic)}</Typography>
                              </Grid>
                            </>
                          )}
                          {item.variants.special?.recyclable !== null && item.variants.special?.recyclable !== undefined && (
                            <>
                              <Grid item xs={5}>
                                <Typography variant="body2" color="text.secondary">Geri Dönüştürülebilir:</Typography>
                              </Grid>
                              <Grid item xs={7}>
                                <Typography variant="body2">{formatVariantValue(item.variants.special.recyclable)}</Typography>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                  )}
                </Grid>
                )}
              </Paper>
            </Grid>
          )}

          {/* Ürün Resimleri */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <PhotoCameraBackIcon sx={{ mr: 1 }} />
                Ürün Resimleri
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {editedItem.imageUrl && editedItem.imageUrl.split(',').filter(Boolean).length > 0 ? (
                <Grid container spacing={2}>
                  {editedItem.imageUrl.split(',').filter(Boolean).map((url, index) => (
                    <Grid item key={index} xs={4} sm={3} md={2}>
                      <Box sx={{ position: 'relative', '&:hover .overlay': { opacity: 1 } }}>
                        <Box
                          component="img"
                          src={url}
                          alt={`Ürün resmi ${index + 1}`}
                          sx={{
                            width: '100%',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: 1,
                            border: '1px solid #ddd',
                          }}
                        />
                        <Box
                          className="overlay"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.3s',
                            borderRadius: 1,
                            cursor: 'pointer',
                          }}
                        >
                          <IconButton onClick={() => handleOpenImagePreview(url)} size="small" sx={{ color: 'white' }}>
                            <VisibilityIcon />
                          </IconButton>
                          {isEditMode && (
                            <IconButton onClick={() => handleDeleteImage(url)} size="small" sx={{ color: 'white' }}>
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Bu ürün için resim eklenmemiş.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {/* ✅ Sadece kapat butonu - düzenleme artık yoktur */}
        <Button onClick={onClose} color="primary">
          {tt('Kapat', 'Close')}
        </Button>
      </DialogActions>

      {/* Resim Önizleme Modalı */}
      <Dialog open={imagePreview.open} onClose={handleCloseImagePreview} maxWidth="md">
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            aria-label="kapat"
            onClick={handleCloseImagePreview}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          <img src={imagePreview.url} alt={tt('Ürün önizleme', 'Product preview')} style={{ width: '100%', display: 'block' }} />
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default InventoryItemDetailModal; 